/**
 * persistence/vcs/StringBridge.js
 *
 * Keeps the active branch's Y.Doc in sync with the live Redux event string.
 *
 * Called fire-and-forget from the persistence middleware on every
 * SET_FULL_ANALYSE_STRING.  It must never block the Redux action, so:
 *   - the live Y.Doc is held in memory (one per session) and only loaded from
 *     Dexie once, lazily;
 *   - reconciling a new string into the Y.Array is a cheap "trim equal ends"
 *     patch — for the common append case this is a single yarr.insert;
 *   - persisting the encoded HEAD back to Dexie is debounced.
 *
 * Why a minimal patch instead of replace-all?
 *   Trimming the common prefix/suffix preserves the CRDT identity of every
 *   unchanged token.  That identity is what lets two analysts' branches merge
 *   cleanly later — replacing the whole array on each keystroke would make
 *   every event look "new" and defeat Yjs convergence.
 *
 * BranchManager is imported lazily (call-time) to avoid a circular dependency:
 *   middleware → StringBridge → BranchManager → StringBridge.getLiveDoc
 */

import { VCS_BRIDGE_DEBOUNCE_MS } from "../config";
import {
  loadDoc,
  encodeDoc,
  setDocTokens,
  stringToTokens,
} from "./yjsDoc";

// ─── Live doc registry ──────────────────────────────────────────────────────

/**
 * Key: `${matchId}__${analystId}` → {
 *   doc:         Y.Doc            live, in-memory
 *   branchName:  string          the active branch this doc represents
 *   ready:       Promise<void>   resolves once the doc is loaded from Dexie
 *   persistTimer: number|null    debounce handle
 * }
 */
const liveDocs = new Map();

function key(matchId, analystId) {
  return `${matchId}__${analystId}`;
}

let _branchManager = null;
async function bm() {
  if (!_branchManager) {
    _branchManager = await import("./BranchManager");
  }
  return _branchManager;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Ensures a live Y.Doc exists for this session, loading the active branch's
 * HEAD state from Dexie on first call.  Idempotent.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<{doc: import('yjs').Doc, branchName: string}>}
 */
export async function ensureLiveDoc(matchId, analystId) {
  const k = key(matchId, analystId);
  const existing = liveDocs.get(k);
  if (existing) {
    await existing.ready;
    return { doc: existing.doc, branchName: existing.branchName };
  }

  // Placeholder entry so concurrent callers share one load.
  const entry = { doc: null, branchName: null, ready: null, persistTimer: null };
  let resolveReady;
  entry.ready = new Promise((res) => (resolveReady = res));
  liveDocs.set(k, entry);

  try {
    const { getOrCreateDefaultBranch } = await bm();
    const branch = await getOrCreateDefaultBranch(matchId, analystId);
    entry.doc = loadDoc(branch?.stateUpdate);
    entry.branchName = branch.branchName;
  } catch (err) {
    console.warn("[StringBridge] ensureLiveDoc failed — using empty doc:", err.message);
    entry.doc = loadDoc(null);
    entry.branchName = null;
  } finally {
    resolveReady();
  }

  return { doc: entry.doc, branchName: entry.branchName };
}

/**
 * Reconciles a new Redux event string into the active branch's Y.Doc.
 * Fire-and-forget — safe to call from the middleware.
 *
 * @param {object} params
 * @param {string} params.matchId
 * @param {string} params.analystId
 * @param {string} params.nextString
 */
export function apply({ matchId, analystId, nextString }) {
  ensureLiveDoc(matchId, analystId)
    .then(({ doc }) => {
      const changed = setDocTokens(doc, stringToTokens(nextString));
      if (changed) schedulePersist(matchId, analystId);
    })
    .catch((err) => {
      console.warn("[StringBridge] apply failed:", err.message);
    });
}

/**
 * Returns the live Y.Doc for a session if one is loaded, else null.
 * Used by BranchManager.commit to snapshot the current working state.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {import('yjs').Doc | null}
 */
export function getLiveDoc(matchId, analystId) {
  return liveDocs.get(key(matchId, analystId))?.doc ?? null;
}

/**
 * Replaces the live doc for a session (e.g. after a checkout/merge that
 * produced a new HEAD) and updates the tracked active branch name.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @param {import('yjs').Doc} doc
 * @param {string} branchName
 */
export function setLiveDoc(matchId, analystId, doc, branchName) {
  const k = key(matchId, analystId);
  const entry = liveDocs.get(k);
  if (entry) {
    entry.doc = doc;
    if (branchName) entry.branchName = branchName;
  } else {
    liveDocs.set(k, {
      doc,
      branchName: branchName ?? null,
      ready: Promise.resolve(),
      persistTimer: null,
    });
  }
}

/**
 * Forces an immediate persist of the live HEAD to Dexie, cancelling any
 * pending debounce.  Call before committing or on exit.
 *
 * @param {string} matchId
 * @param {string} analystId
 * @returns {Promise<void>}
 */
export async function flush(matchId, analystId) {
  const k = key(matchId, analystId);
  const entry = liveDocs.get(k);
  if (!entry?.doc) return;
  if (entry.persistTimer) {
    clearTimeout(entry.persistTimer);
    entry.persistTimer = null;
  }
  await persistNow(matchId, analystId, entry);
}

// ─── Internal ─────────────────────────────────────────────────────────────────

function schedulePersist(matchId, analystId) {
  const k = key(matchId, analystId);
  const entry = liveDocs.get(k);
  if (!entry) return;
  if (entry.persistTimer) clearTimeout(entry.persistTimer);
  entry.persistTimer = setTimeout(() => {
    entry.persistTimer = null;
    persistNow(matchId, analystId, entry).catch((err) =>
      console.warn("[StringBridge] persist failed:", err.message)
    );
  }, VCS_BRIDGE_DEBOUNCE_MS);
}

async function persistNow(matchId, analystId, entry) {
  if (!entry?.doc || !entry.branchName) return;
  const { updateBranchHead } = await bm();
  await updateBranchHead({
    matchId,
    analystId,
    branchName: entry.branchName,
    stateUpdate: encodeDoc(entry.doc),
  });
}
